import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { PostHogProvider } from 'posthog-js/react';
import * as Sentry from "@sentry/react";

// Extend Window interface for testSentry
declare global {
  interface Window {
    testSentry: () => void;
  }
}

// Initialize Sentry only in production (not localhost)
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

if (!isLocalhost) {
  console.log('🚀 Initializing Sentry for production...');
  Sentry.init({
    dsn: "https://bdcb4da3ab0139b2b8bf01be0d8e962f@o4510459450228736.ingest.de.sentry.io/4510459452653648",
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    release: process.env.REACT_APP_VERSION || '1.0.0',
    sendDefaultPii: true,
    // Capture 100% of errors
    sampleRate: 1.0,
    // Capture network errors specifically
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      // Log all errors to console for debugging
      console.log('🚨 Sentry capturing error:', event, hint);
      return event;
    },
    // Capture 100% of the transactions for performance monitoring
    tracesSampleRate: 1.0,
    // Capture session replay on errors
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
  });

  // Test Sentry in console (production only)
  console.log('🧪 Sentry test - trigger error with: window.testSentry()');
  window.testSentry = () => Sentry.captureException(new Error("Manual test error from console"));
} else {
  console.log('🏠 Localhost detected - Sentry disabled');
  // Mock Sentry for localhost to prevent errors
  window.testSentry = () => console.log('🏠 Sentry disabled on localhost');
}

const options = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  autocapture: true,
  capture_pageview: true,
  capture_pageleave: true,
  enable_recording_console_log: true, // Capture console errors
  // Disable features that require /s/ and /e/ endpoints
  disable_session_recording: true,
  disable_surveys: true,
  disable_compression: true,
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const AppWrapper = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return <App />;
  }

  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_KEY || ''}
      options={options}
    >
      <App />
    </PostHogProvider>
  );
};

root.render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
);

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
